import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "../lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  // null = checking, object = authed, false = not authed
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const login = useCallback(async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    setUser(data);
    return data;
  }, []);

  const register = useCallback(async (email, password, name) => {
    const { data } = await api.post("/auth/register", { email, password, name });
    setUser(data);
    return data;
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch (err) {
      // Logout is best-effort: cookie may already be expired or network may be flaky.
      // Surface a warning for debugging but never block the local sign-out below.
      console.warn("Logout request failed; proceeding with local sign-out.", err);
    }
    setUser(false);
  }, []);

  const saveOnboarding = useCallback(async (payload) => {
    const { data } = await api.post("/auth/onboarding", payload);
    setUser(data);
    return data;
  }, []);

  const resendVerification = useCallback(async () => {
    await api.post("/auth/resend-verification");
  }, []);

  const value = useMemo(
    () => ({ user, setUser, loading, refresh, login, register, logout, saveOnboarding, resendVerification, formatApiError }),
    [user, loading, refresh, login, register, logout, saveOnboarding, resendVerification]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  return useContext(AuthContext);
}
